import { NetworkStatus } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import sortBy from "lodash-es/sortBy";
import { useMemo } from "react";

import { Stack, StackContextAttachment } from "types/generated";

import { GET_ATTACHED_CONTEXTS } from "./gql";

type AttachedStacks<IsAutoAttached extends boolean> = Array<
  Omit<StackContextAttachment, "isAutoattached"> & {
    isAutoattached: IsAutoAttached;
  }
>;

const useAttachedContexts = (stackId?: string) => {
  const { networkStatus, data, loading, refetch, error } = useQuery<{ stack: Stack }>(
    GET_ATTACHED_CONTEXTS,
    {
      variables: { stackId },
      fetchPolicy: "no-cache",
      skip: !stackId,
    }
  );

  const [manuallyAttachedContexts, autoAttachedContexts, manuallyAttachedContextIds, allContexts] =
    useMemo(() => {
      const contexts = sortBy(data?.stack?.attachedContexts || [], "priority");
      const manuallyAttachedContextIds: string[] = [];
      const manuallyAttachedContexts: AttachedStacks<false> = [];
      const autoAttachedContexts: AttachedStacks<true> = [];

      for (const context of contexts) {
        if (context.isAutoattached) {
          autoAttachedContexts.push({
            ...context,
            isAutoattached: true,
          });
        } else {
          manuallyAttachedContextIds.push(context.contextId);

          manuallyAttachedContexts.push({
            ...context,
            isAutoattached: false,
          });
        }
      }

      return [
        manuallyAttachedContexts,
        autoAttachedContexts,
        manuallyAttachedContextIds,
        [...manuallyAttachedContexts, ...autoAttachedContexts],
      ];
    }, [data?.stack?.attachedContexts]);

  const isLoading = loading && networkStatus !== NetworkStatus.refetch;
  const isRefetching = loading && networkStatus === NetworkStatus.refetch;

  return {
    allContexts,
    manuallyAttachedContexts,
    autoAttachedContexts,
    manuallyAttachedContextIds,
    loading: isLoading,
    refetching: isRefetching,
    hasData: !isLoading && !!data?.stack,
    refetch,
    error,
  };
};

export default useAttachedContexts;
